import React, { useId, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'components/Button';
import { uploadUniformDocument, openUniformDocument, uniformErrorMessage } from 'api/services/uniformManagement';

/**
 * Reusable attachment control for PO / invoice / transfer documents.
 *
 * - No value yet: shows a file chooser (accepts pdf/jpg/jpeg/png) with an upload spinner.
 * - Has a value: shows the file name with View / Download / Remove actions.
 *   View and Download are hidden when `fetchDocumentApi` is not supplied (i.e. the
 *   parent record hasn't been saved yet, so there is no signed-URL endpoint to call).
 *
 * Props:
 *   label            string                                 - field label
 *   value            { document_url, document_name } | null  - current attachment
 *   onChange         (valueOrNull) => void                    - called after upload / remove
 *   fetchDocumentApi (params) => Promise                      - optional signed-url fetcher
 *   disabled         boolean
 *   className        string                                   - wrapper classes; defaults to
 *                    `mb-3`. Pass `''` where the surrounding section already owns the spacing
 *                    (e.g. a `.detail-section`), so the control does not add a second gap.
 */
const DocumentAttachment = ({ label, value, onChange, fetchDocumentApi, disabled, className = 'mb-3' }) => {
  const labelId = useId();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // "view" | "download" | null

  const isBusy = uploading || !!actionLoading;

  const handleChooseFile = () => {
    if (disabled || isBusy) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadUniformDocument(file);
      onChange(result);
    } catch (err) {
      toast.error(uniformErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = async (disposition) => {
    if (!fetchDocumentApi) return;
    setActionLoading(disposition === 'inline' ? 'view' : 'download');
    try {
      await openUniformDocument(fetchDocumentApi, disposition);
    } catch (err) {
      toast.error(uniformErrorMessage(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = () => {
    if (disabled) return;
    onChange(null);
  };

  return (
    <div className={className}>
      {label && (
        <label
          id={labelId}
          style={{
            display: 'block',
            fontSize: 12.5,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
            letterSpacing: '0.01em',
          }}
        >
          {label}
        </label>
      )}

      <div role="group" aria-labelledby={label ? labelId : undefined}>
        <input
          ref={inputRef}
          type="file"
          className="d-none"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          disabled={disabled || isBusy}
        />

        {!value ? (
          <Button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            icon="fa fa-upload"
            label={uploading ? 'Uploading...' : 'Upload File'}
            loading={uploading}
            disabled={disabled || isBusy}
            onClick={handleChooseFile}
          />
        ) : (
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: 8, border: '1px solid #d9d9d9', borderRadius: 6, padding: '6px 10px' }}
          >
            <i className="fa fa-file-lines" aria-hidden="true" style={{ color: '#6b7280' }} />
            <span
              className="text-truncate"
              style={{ maxWidth: 240, fontSize: 13, color: '#374151' }}
              title={value.document_name}
            >
              {value.document_name}
            </span>

            <div className="d-flex align-items-center ms-auto" style={{ gap: 6 }}>
              {fetchDocumentApi && (
                <>
                  <Button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    label="View"
                    loading={actionLoading === 'view'}
                    disabled={disabled || isBusy}
                    onClick={() => handleOpen('inline')}
                  />
                  <Button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    label="Download"
                    loading={actionLoading === 'download'}
                    disabled={disabled || isBusy}
                    onClick={() => handleOpen('attachment')}
                  />
                </>
              )}
              <Button
                type="button"
                className="btn btn-link btn-sm p-0 text-danger"
                label="Remove"
                disabled={disabled || isBusy}
                onClick={handleRemove}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentAttachment;
